<template>
  <v-dialog
    :model-value="modelValue"
    max-width="900"
    persistent
    scrollable
    @update:model-value="closeDialog"
  >
    <v-card class="ncrm-file-import">
      <!-- ── Header ──────────────────────────────────────────────────────── -->
      <v-card-title class="d-flex align-center justify-space-between pa-4">
        <div>
          <div class="text-h6 ncrm-text-strong">
            {{ $t("ncrm.workflows.fileImport.title") }}
          </div>
          <div class="text-caption text-medium-emphasis mt-1">
            {{ $t(`ncrm.workflows.fileImport.steps.${currentStepKey}.subtitle`) }}
          </div>
        </div>
        <v-btn icon="mdi-close" variant="text" size="small" @click="closeDialog(false)" />
      </v-card-title>

      <!-- ── Stepper ─────────────────────────────────────────────────────── -->
      <div class="ncrm-file-import__stepper px-4 pb-2">
        <div
          v-for="(step, idx) in steps"
          :key="step.key"
          class="ncrm-file-import__step"
          :class="{
            'ncrm-file-import__step--active': step.key === currentStepKey,
            'ncrm-file-import__step--done': stepIndex > idx,
          }"
        >
          <span class="ncrm-file-import__step-num">{{ idx + 1 }}</span>
          <span class="ncrm-file-import__step-label">{{ $t(step.labelKey) }}</span>
        </div>
      </div>

      <v-divider />

      <v-card-text class="pa-4 ncrm-file-import__body">
        <!-- ── Pas 1: Upload ─────────────────────────────────────────────── -->
        <template v-if="currentStepKey === 'upload'">
          <v-alert type="info" density="compact" class="mb-4">
            {{ $t("ncrm.workflows.fileImport.upload.hint") }}
          </v-alert>

          <div
            class="ncrm-file-import__dropzone"
            :class="{ 'ncrm-file-import__dropzone--dragging': dragging }"
            @dragover.prevent="dragging = true"
            @dragleave.prevent="dragging = false"
            @drop.prevent="onDrop"
            @click="$refs.fileInput.click()"
          >
            <v-icon size="48" color="primary">mdi-cloud-upload-outline</v-icon>
            <div class="text-body-1 mt-2">
              {{
                selectedFile
                  ? selectedFile.name
                  : $t("ncrm.workflows.fileImport.upload.dropOrClick")
              }}
            </div>
            <div class="text-caption text-medium-emphasis mt-1">
              {{ $t("ncrm.workflows.fileImport.upload.acceptedFormats") }}
            </div>
            <input
              ref="fileInput"
              type="file"
              accept=".csv,.xlsx,.json,.sql"
              class="ncrm-file-import__file-input"
              @change="onFilePicked"
            />
          </div>

          <v-alert
            v-if="uploadError"
            type="error"
            density="compact"
            class="mt-3"
          >
            {{ uploadError }}
          </v-alert>
        </template>

        <!-- ── Pas 2: Preview & Mapping ──────────────────────────────────── -->
        <template v-else-if="currentStepKey === 'mapping'">
          <div class="d-flex align-center ga-2 mb-3">
            <v-chip color="success">
              <v-icon start size="14">mdi-check-circle</v-icon>
              {{ batch?.file_name }}
            </v-chip>
            <v-chip color="info">
              {{ $t("ncrm.workflows.fileImport.mapping.totalRows", { n: preview?.totalRows || 0 }) }}
            </v-chip>
            <v-chip v-if="reused" color="warning">
              <v-icon start size="14">mdi-refresh</v-icon>
              {{ $t("ncrm.workflows.fileImport.mapping.reused") }}
            </v-chip>
          </div>

          <div class="text-caption font-weight-semibold text-uppercase text-medium-emphasis mb-2">
            {{ $t("ncrm.workflows.fileImport.mapping.title") }}
          </div>
          <v-alert type="info" density="compact" class="mb-3 text-body-2">
            {{ $t("ncrm.workflows.fileImport.mapping.hint") }}
          </v-alert>

          <div class="ncrm-file-import__mapping-grid">
            <div
              v-for="header in preview?.headers || []"
              :key="header"
              class="ncrm-file-import__mapping-row"
            >
              <div class="ncrm-file-import__mapping-source">
                <div class="text-body-2 font-weight-medium">{{ header }}</div>
                <div class="text-caption text-medium-emphasis">
                  {{ sampleValueFor(header) }}
                </div>
              </div>
              <v-icon size="16" color="medium-emphasis">mdi-arrow-right</v-icon>
              <v-autocomplete
                v-model="mappingConfig[header]"
                :items="crmFieldOptions"
                item-title="label"
                item-value="value"
                :placeholder="$t('ncrm.workflows.fileImport.mapping.skipPlaceholder')"
                density="compact"
                hide-details
                clearable
                auto-select-first
                :menu-props="{ maxHeight: 320, zIndex: 2710 }"
                :no-data-text="$t('ncrm.workflows.fileImport.mapping.noMatchingField')"
              />
            </div>
          </div>

          <v-alert
            v-if="executeError"
            type="error"
            density="compact"
            class="mt-3"
          >
            {{ executeError }}
          </v-alert>
        </template>

        <!-- ── Pas 3: Rezultat ───────────────────────────────────────────── -->
        <template v-else-if="currentStepKey === 'result'">
          <div class="ncrm-file-import__result">
            <v-icon
              :color="executionResult?.errors?.length ? 'warning' : 'success'"
              size="64"
            >
              {{
                executionResult?.errors?.length
                  ? "mdi-alert-circle-outline"
                  : "mdi-check-circle-outline"
              }}
            </v-icon>
            <div class="text-h6 mt-3">
              {{
                $t("ncrm.workflows.fileImport.result.summary", {
                  imported: executionResult?.imported || 0,
                  total: executionResult?.total || 0,
                })
              }}
            </div>
            <div v-if="executionResult?.skipped" class="text-body-2 text-medium-emphasis mt-1">
              {{
                $t("ncrm.workflows.fileImport.result.skipped", {
                  n: executionResult.skipped,
                })
              }}
            </div>
            <v-alert
              v-if="executionResult?.errors?.length"
              type="warning"
              class="mt-4 text-left"
              max-width="640"
            >
              <div class="font-weight-medium mb-1">
                {{ $t("ncrm.workflows.fileImport.result.errorsTitle") }}
              </div>
              <ul class="ncrm-file-import__errors">
                <li v-for="err in (executionResult.errors || []).slice(0, 5)" :key="err.row_index">
                  Rând {{ err.row_index }}: {{ err.error }}
                </li>
                <li v-if="executionResult.errors.length > 5" class="text-caption">
                  …{{ executionResult.errors.length - 5 }} mai multe erori.
                </li>
              </ul>
            </v-alert>

            <!-- Sumar auto-dedup (când e activat în setările nodului) -->
            <v-alert
              v-if="dedupSummary"
              :type="dedupSummary.error ? 'warning' : 'success'"
              class="mt-3 text-left"
              max-width="640"
            >
              <div class="font-weight-medium mb-1">
                <v-icon start size="16">mdi-shuffle-variant</v-icon>
                {{ $t("ncrm.workflows.fileImport.result.dedupTitle") }}
              </div>
              <div v-if="dedupSummary.error" class="text-body-2">
                {{ dedupSummary.error }}
              </div>
              <div v-else class="text-body-2">
                {{ $t("ncrm.workflows.fileImport.result.dedupSummary", {
                  processed: dedupSummary.processed || 0,
                  duplicates: dedupSummary.duplicate_found || 0,
                  review: dedupSummary.pending_review || 0,
                  newRecords: dedupSummary.pending_enrichment || 0,
                }) }}
              </div>
            </v-alert>
          </div>
        </template>
      </v-card-text>

      <v-divider />

      <!-- ── Footer ──────────────────────────────────────────────────────── -->
      <v-card-actions class="pa-4 d-flex justify-end ga-2">
        <template v-if="currentStepKey === 'upload'">
          <v-btn variant="text" @click="closeDialog(false)">
            {{ $t("app.actions.cancel") }}
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!selectedFile"
            :loading="uploading"
            @click="uploadAndPreview"
          >
            {{ $t("ncrm.workflows.fileImport.upload.action") }}
          </v-btn>
        </template>

        <template v-else-if="currentStepKey === 'mapping'">
          <v-btn variant="text" @click="goBackToUpload">
            <v-icon start>mdi-arrow-left</v-icon>
            {{ $t("ncrm.workflows.fileImport.mapping.backToUpload") }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="executing"
            @click="execute"
          >
            <v-icon start>mdi-database-arrow-up-outline</v-icon>
            {{ $t("ncrm.workflows.fileImport.mapping.action") }}
          </v-btn>
        </template>

        <template v-else>
          <v-btn color="primary" variant="flat" @click="closeDialog(true)">
            {{ $t("app.actions.close") }}
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  uploadStagingFile,
  executeStagingFileImport,
} from "@/Utile/ncrmStagingFileImportApi.mjs";
import { buildCrmRemapOptions, CRM_REMAP_COLUMNS } from "./NcrmWorkflowStagingFields.mjs";

const STEPS = [
  { key: "upload", labelKey: "ncrm.workflows.fileImport.steps.upload.label" },
  { key: "mapping", labelKey: "ncrm.workflows.fileImport.steps.mapping.label" },
  { key: "result", labelKey: "ncrm.workflows.fileImport.steps.result.label" },
];

export default {
  name: "NcrmWorkflowFileImportDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    workflowInstanceId: { type: String, required: true },
  },
  emits: ["update:modelValue", "imported"],
  data() {
    return {
      steps: STEPS,
      currentStepKey: "upload",
      selectedFile: null,
      dragging: false,
      uploading: false,
      uploadError: null,
      batch: null,
      preview: null,
      reused: false,
      mappingConfig: {},
      executing: false,
      executeError: null,
      executionResult: null,
    };
  },
  computed: {
    stepIndex() {
      return STEPS.findIndex((s) => s.key === this.currentStepKey);
    },
    crmFieldOptions() {
      return buildCrmRemapOptions(this.$t.bind(this));
    },
    dedupSummary() {
      // Backend întoarce executionResult.dedup când autoDedupAfterImport === true.
      // null → nu a rulat (setarea e off).
      // { error } → a eșuat dedup-ul (importul a reușit oricum).
      // { processed, duplicate_found, pending_review, pending_enrichment } → succes.
      return this.executionResult?.dedup ?? null;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.resetState();
    },
  },
  methods: {
    resetState() {
      this.currentStepKey = "upload";
      this.selectedFile = null;
      this.uploadError = null;
      this.batch = null;
      this.preview = null;
      this.reused = false;
      this.mappingConfig = {};
      this.executing = false;
      this.executeError = null;
      this.executionResult = null;
    },
    closeDialog(emitImported = false) {
      if (emitImported && this.executionResult) {
        this.$emit("imported", this.executionResult);
      }
      this.$emit("update:modelValue", false);
    },
    onDrop(event) {
      this.dragging = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) this.handlePickedFile(file);
    },
    onFilePicked(event) {
      const file = event.target.files?.[0];
      if (file) this.handlePickedFile(file);
    },
    handlePickedFile(file) {
      const allowed = [".csv", ".xlsx", ".json", ".sql"];
      const lower = file.name.toLowerCase();
      const ok = allowed.some((ext) => lower.endsWith(ext));
      if (!ok) {
        this.uploadError = this.$t("ncrm.workflows.fileImport.errors.unsupportedFormat");
        this.selectedFile = null;
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        this.uploadError = this.$t("ncrm.workflows.fileImport.errors.tooLarge");
        this.selectedFile = null;
        return;
      }
      this.uploadError = null;
      this.selectedFile = file;
    },
    async uploadAndPreview() {
      if (!this.selectedFile) return;
      this.uploading = true;
      this.uploadError = null;
      try {
        const { data } = await uploadStagingFile(this.selectedFile, this.workflowInstanceId);
        this.batch = data?.batch || null;
        this.preview = data?.preview || { headers: [], sampleRows: [], totalRows: 0 };
        this.reused = !!data?.reused;
        this.autoSuggestMapping();
        this.currentStepKey = "mapping";
      } catch (err) {
        this.uploadError =
          err?.response?.data?.error ||
          this.$t("ncrm.workflows.fileImport.errors.uploadFailed");
      } finally {
        this.uploading = false;
      }
    },
    /**
     * Auto-sugerează mapping pentru headers care se potrivesc exact cu o coloană CRM
     * (case-insensitive, după trim). Userul poate suprascrie.
     */
    autoSuggestMapping() {
      const lookup = new Map(CRM_REMAP_COLUMNS.map((c) => [c.toLowerCase(), c]));
      const cfg = {};
      for (const header of this.preview?.headers || []) {
        const norm = String(header).trim().toLowerCase();
        if (lookup.has(norm)) cfg[header] = lookup.get(norm);
      }
      this.mappingConfig = cfg;
    },
    sampleValueFor(header) {
      const row = this.preview?.sampleRows?.[0];
      if (!row) return "—";
      const val = row[header];
      if (val === null || val === undefined || val === "") return "—";
      const str = typeof val === "object" ? JSON.stringify(val) : String(val);
      return str.length > 60 ? `${str.slice(0, 57)}…` : str;
    },
    goBackToUpload() {
      this.currentStepKey = "upload";
      this.executeError = null;
    },
    async execute() {
      if (!this.batch?.id || !this.selectedFile) return;
      this.executing = true;
      this.executeError = null;
      try {
        // Filtrăm mappingConfig să păstrăm doar pairs valide
        const cleanMapping = Object.fromEntries(
          Object.entries(this.mappingConfig).filter(([, v]) => v)
        );
        const { data } = await executeStagingFileImport(
          this.batch.id,
          this.selectedFile,
          cleanMapping,
          this.workflowInstanceId
        );
        this.executionResult = data || null;
        this.currentStepKey = "result";
      } catch (err) {
        this.executeError =
          err?.response?.data?.error ||
          this.$t("ncrm.workflows.fileImport.errors.executeFailed");
      } finally {
        this.executing = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-file-import {
  background: rgb(var(--v-theme-surface));
}

.ncrm-file-import__body {
  min-height: 320px;
  max-height: 70vh;
}

/* Stepper */
.ncrm-file-import__stepper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ncrm-file-import__step {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  color: var(--app-text-default);
  opacity: 0.4;
  transition: opacity 0.15s ease;
}

.ncrm-file-import__step--active { opacity: 1; }
.ncrm-file-import__step--done   { opacity: 0.7; }

.ncrm-file-import__step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(var(--v-theme-on-surface), 0.08);
  font-weight: 600;
  font-size: 0.75rem;
}

.ncrm-file-import__step--active .ncrm-file-import__step-num {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
}

.ncrm-file-import__step--done .ncrm-file-import__step-num {
  background: rgb(var(--v-theme-success));
  color: rgb(var(--v-theme-on-success));
}

/* Dropzone */
.ncrm-file-import__dropzone {
  border: 2px dashed rgba(var(--v-theme-on-surface), 0.18);
  border-radius: 10px;
  padding: 32px 24px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.ncrm-file-import__dropzone:hover,
.ncrm-file-import__dropzone--dragging {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.04);
}

.ncrm-file-import__file-input {
  display: none;
}

/* Mapping grid */
.ncrm-file-import__mapping-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ncrm-file-import__mapping-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.02);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.05);
}

.ncrm-file-import__mapping-source {
  min-width: 0;
  overflow: hidden;
}

.ncrm-file-import__mapping-source > .text-body-2 {
  word-break: break-word;
}

.ncrm-file-import__mapping-source > .text-caption {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Result */
.ncrm-file-import__result {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 16px;
}

.ncrm-file-import__errors {
  margin: 0;
  padding-left: 18px;
  font-size: 0.82rem;
}
</style>
